import api from './client';
import type { AvailabilityBlock, AvailabilityBlockType, BlockRecurrence, ZoneType } from "@store/disponibiliteStore";
import { getBlockRecurrence } from "@store/disponibiliteStore";

/* ── API shape (snake_case) ── */

interface ApiDisponibiliteBlock {
  id: string;
  org_id: string;
  worker_id: string;
  worker_name: string;
  type: "recurring" | "ponctuel";
  block_type: string;
  day_of_week?: number;
  date?: string;
  /** End date (inclusive) for multi-day blocks. */
  end_date?: string;
  /** Full-amplitude "toute la journée" block. */
  all_day?: boolean;
  /** Recurrence rule: none | weekly | annual. */
  recurrence?: BlockRecurrence;
  start_hour: number;
  end_hour: number;
  label: string;
  color: string;
  category: "disponibilite" | "service_rapide";
  zone_type?: string;
  absence_kind?: string;
}

/* ── Converters ── */

function toFrontend(b: ApiDisponibiliteBlock): AvailabilityBlock {
  const recurrence: BlockRecurrence = b.recurrence || (b.type === "recurring" ? "weekly" : "none");
  // A weekly block's « Au » bounds the recurrence window — each occurrence stays a
  // single-day slot. Read as a multi-day span it got durationHours=0 and drew as an
  // empty line (Emeric, lundi 8h-12h du 06/07 au 31/12, 2026-10-14).
  const multiDay = recurrence !== "weekly" && !!b.end_date && b.end_date > (b.date || "");
  return {
    id: b.id,
    workerId: b.worker_id,
    label: b.label,
    type: (b.block_type || "off") as AvailabilityBlockType,
    date: b.date || "",
    endDate: b.end_date || undefined,
    allDay: b.all_day || undefined,
    startHour: b.start_hour,
    // Single-day duration; multi-day spans use endHour/endDate instead.
    durationHours: multiDay ? 0 : b.end_hour - b.start_hour,
    endHour: multiDay ? b.end_hour : undefined,
    recurring: recurrence === "weekly",
    recurrence,
    dayOfWeek: b.day_of_week,
    zoneType: (b.zone_type as ZoneType) || undefined,
    absenceKind: b.absence_kind === "formation" ? "formation" : undefined,
  };
}

function toApi(b: Omit<AvailabilityBlock, "id">, orgId: string, category: string, workerName: string = ""): Omit<ApiDisponibiliteBlock, "id"> {
  const recurrence = getBlockRecurrence(b);
  return {
    org_id: orgId,
    worker_id: b.workerId,
    worker_name: workerName,
    type: recurrence === "weekly" ? "recurring" : "ponctuel",
    recurrence,
    block_type: b.type,
    day_of_week: b.dayOfWeek,
    date: b.date || undefined,
    end_date: b.endDate || undefined,
    all_day: b.allDay || undefined,
    start_hour: b.startHour,
    end_hour: b.endHour ?? b.startHour + b.durationHours,
    label: b.label,
    color: "#3b82f6",
    category: category as "disponibilite" | "service_rapide",
    zone_type: b.zoneType,
    absence_kind: b.absenceKind || "",
  };
}

function toApiUpdate(updates: Partial<Omit<AvailabilityBlock, "id">>): Record<string, any> {
  const out: Record<string, any> = {};
  if (updates.workerId !== undefined) out.worker_id = updates.workerId;
  if (updates.label !== undefined) out.label = updates.label;
  if (updates.type !== undefined) out.block_type = updates.type;
  if (updates.recurrence !== undefined) {
    out.recurrence = updates.recurrence;
    out.type = updates.recurrence === "weekly" ? "recurring" : "ponctuel";
  } else if (updates.recurring !== undefined) {
    out.type = updates.recurring ? "recurring" : "ponctuel";
    out.recurrence = updates.recurring ? "weekly" : "none";
  }
  if (updates.dayOfWeek !== undefined) out.day_of_week = updates.dayOfWeek;
  if (updates.date !== undefined) out.date = updates.date;
  if (updates.endDate !== undefined) out.end_date = updates.endDate || null;
  if (updates.allDay !== undefined) out.all_day = updates.allDay;
  if (updates.startHour !== undefined) out.start_hour = updates.startHour;
  if (updates.endHour !== undefined) {
    out.end_hour = updates.endHour;
  } else if (updates.durationHours !== undefined && updates.startHour !== undefined) {
    out.end_hour = updates.startHour + updates.durationHours;
  } else if (updates.durationHours !== undefined) {
    out.end_hour = updates.durationHours; // fallback
  }
  if (updates.zoneType !== undefined) out.zone_type = updates.zoneType;
  if ("absenceKind" in updates) out.absence_kind = updates.absenceKind || "";
  return out;
}

/* ── API calls ── */

export const disponibilitesApi = {
  list: async (params?: { org_id?: string; worker_id?: string; category?: string; limit?: number }): Promise<AvailabilityBlock[]> => {
    const { data } = await api.get('/disponibilites', { params });
    return (data as ApiDisponibiliteBlock[]).map(toFrontend);
  },

  create: async (block: Omit<AvailabilityBlock, "id">, orgId: string, category: string, workerName?: string): Promise<AvailabilityBlock> => {
    const payload = toApi(block, orgId, category, workerName);
    const { data } = await api.post('/disponibilites', payload);
    return toFrontend(data);
  },

  update: async (id: string, updates: Partial<Omit<AvailabilityBlock, "id">>): Promise<AvailabilityBlock> => {
    const payload = toApiUpdate(updates);
    const { data } = await api.patch(`/disponibilites/${id}`, payload);
    return toFrontend(data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/disponibilites/${id}`);
  },
};
